import React, { useState, Suspense } from 'react';
import {
  BrowserRouter as Router,
  Navigate,
  Route,
  Routes,
} from 'react-router-dom';
import { MsalProvider } from '@azure/msal-react';
import {
  HOME_PATH,
  STARTING_PATH,
  ACCOUNT_SETUP_PATH,
  PREFERENCES_PATH,
  PROFILE_PATH,
  NOT_APPROVED_PATH,
  NOT_AUTHORISED_PATH,
  ADMIN_PATH,
  getAdminPath,
  REPORTS_DASHBOARD_V2_PATH,
  AI_TOOLKIT_PATH,
  CREATE_GPT_JOB_PATH,
  getAIToolkitPath,
  SPECIALIST_MARKETPLACE_PATH,
  KNOWLEDGE_HUB_PATH,
  SPECIALIST_MARKETPLACE_SHARED_PATH,
  FEEDBACK_PATH,
  LINK_FORGE_PATH,
  CREATIVE_GENERATION_PATH,
  CREATIVE_VOLUME_PLANNER_PATH,
  REPORTS_INSIGHTS_PATH,
} from '../utils/paths';

const ScreensHome = React.lazy(async () => import('./Home'));
const ScreensAIToolkit = React.lazy(async () => import('./AIToolkit'));
const ScreensViewGptJob = React.lazy(async () => import('./ViewGptJob'));
const ScreensCreateGptJob = React.lazy(async () => import('./CreateGptJob'));
const ScreensNotFound = React.lazy(async () => import('./NotFound/NotFound'));
const ScreensAdminPanel = React.lazy(async () => import('./AdminPanel'));
const AddesuLayout = React.lazy(
  async () => import('../components/UI/AddesuLayout')
);
const ScreensUserProfile = React.lazy(async () => import('./UserProfile'));
const ScreensPreferences = React.lazy(async () => import('./Preferences'));
const ScreensNotApproved = React.lazy(
  async () => import('./NotApproved/NotApproved')
);
const ScreensAccountSetup = React.lazy(async () => import('./AccountSetup'));
const ScreensNotAuthorised = React.lazy(
  async () => import('./NotAuthorised/NotAuthorised')
);
const ScreensSpecialistMarketplace = React.lazy(
  async () =>
    import('./NotionScreens/SpecialistMarketplaceScreens/SpecialistMarketplace')
);

const ScreenFeedback = React.lazy(async () => import('./Feedback'));

const ScreensSpecialistMarketplaceShared = React.lazy(
  async () =>
    import(
      './NotionScreens/SpecialistMarketplaceScreens/SpecialistMarketplaceShared'
    )
);
const ScreensKnowledgeHub = React.lazy(
  async () => import('./NotionScreens/KnowledgeHubScreens/KnowledgeHubPage')
);
const ScreensKnowledgeHubDatabase = React.lazy(
  async () => import('./NotionScreens/KnowledgeHubScreens/KnowledgeHubDatabase')
);

const ScreenlinkForge = React.lazy(async () => import('./Tools/LinkForge'));

const ScreenGreativeGeneration = React.lazy(
  async () => import('./Tools/CreativeGeneration')
);

const ScreenCreativeVolumeChecker = React.lazy(
  async () => import('./Tools/CreativeVolumeChecker')
);

const ScreensDashboard = React.lazy(async () => import('./Reports/Dashboards'));

const ScreensAnalysis = React.lazy(async () => import('./Reports/Analysis'));

import { msalInstance } from '../utils/authProvider';
import ProtectedRoute from '../components/ProtectedRoute/ProtectedRoute';
import InfoModal from '../components/UI/InfoModal';
import { useCustomer } from '../domains/customers';
import { AdminType } from '../domains/admin';
import { AIToolkitOptions } from '../domains/ai-toolkit';
import { AvailableFeature } from '../domains/features/types';
import CustomFeatureGuard from '../components/CustomFeatureGuard/CustomFeatureGuard';

const ScreensRoot: React.FC = () => {
  const { customer, isCustomerFetching } = useCustomer();
  const [isModalOpen, setIsModalOpen] = useState(true);

  const isMobileBrowser = window.innerWidth < 1024;

  return (
    <Router>
      <Suspense fallback={<div>.</div>}>
        <MsalProvider instance={msalInstance}>
          <AddesuLayout>
            {isMobileBrowser && (
              <InfoModal
                isModalOpen={isModalOpen}
                handleClose={() => setIsModalOpen(false)}
              />
            )}
            <Routes>
              {customer?.isAdmin && (
                <Route
                  path={HOME_PATH}
                  element={<Navigate to={getAdminPath(AdminType.USERS)} />}
                />
              )}
              <Route
                path={STARTING_PATH}
                element={<Navigate to={HOME_PATH} />}
              />
              <Route
                path={HOME_PATH}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensHome />
                  </ProtectedRoute>
                }
              />
              <Route
                path={CREATE_GPT_JOB_PATH}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensCreateGptJob />
                  </ProtectedRoute>
                }
              />
              <Route
                path={ACCOUNT_SETUP_PATH}
                element={<ScreensAccountSetup />}
              />
              <Route path={PREFERENCES_PATH} element={<ScreensPreferences />} />
              <Route
                path={`${AI_TOOLKIT_PATH}/:tool`}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensAIToolkit />
                  </ProtectedRoute>
                }
              />
              <Route
                path={LINK_FORGE_PATH}
                element={
                  <CustomFeatureGuard
                    auth={customer?.isApproved}
                    customerId={customer?.customerId as string}
                    featureName={AvailableFeature.PROTON}
                    isPending={isCustomerFetching}
                  >
                    <ScreenlinkForge />
                  </CustomFeatureGuard>
                }
              />
              <Route
                path={CREATIVE_GENERATION_PATH}
                element={
                  <CustomFeatureGuard
                    auth={customer?.isApproved}
                    customerId={customer?.customerId as string}
                    featureName={AvailableFeature.CREATIVE_GENERATION}
                    isPending={isCustomerFetching}
                  >
                    <ScreenGreativeGeneration />
                  </CustomFeatureGuard>
                }
              />
              <Route
                path={CREATIVE_VOLUME_PLANNER_PATH}
                element={
                  <CustomFeatureGuard
                    auth={customer?.isApproved}
                    customerId={customer?.customerId as string}
                    featureName={AvailableFeature.CREATIVE_VOLUME_PLANNER}
                    isPending={isCustomerFetching}
                  >
                    <ScreenCreativeVolumeChecker />
                  </CustomFeatureGuard>
                }
              />
              <Route
                path={`${REPORTS_DASHBOARD_V2_PATH}/:customerId?`}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensDashboard />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${REPORTS_INSIGHTS_PATH}/*`}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensAnalysis />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${getAIToolkitPath(AIToolkitOptions.TITLE_OPTIMIZATION)}/:jobId`}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensViewGptJob />
                  </ProtectedRoute>
                }
              />
              <Route
                path={SPECIALIST_MARKETPLACE_PATH}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensSpecialistMarketplace />
                  </ProtectedRoute>
                }
              />
              <Route
                path={SPECIALIST_MARKETPLACE_SHARED_PATH}
                element={<ScreensSpecialistMarketplaceShared />}
              />
              <Route
                path={ADMIN_PATH}
                element={
                  <ProtectedRoute
                    auth={customer?.isApproved}
                    isPending={isCustomerFetching}
                  >
                    <ScreensAdminPanel />
                  </ProtectedRoute>
                }
              />
              <Route
                path={`${PROFILE_PATH}/:customerId?`}
                element={<ScreensUserProfile />}
              />
              <Route
                path={KNOWLEDGE_HUB_PATH}
                element={<ScreensKnowledgeHubDatabase />}
              />
              <Route
                path={`${KNOWLEDGE_HUB_PATH}/:pageId?`}
                element={<ScreensKnowledgeHub />}
              />
              <Route path={FEEDBACK_PATH} element={<ScreenFeedback />} />
              <Route
                path={NOT_APPROVED_PATH}
                element={<ScreensNotApproved />}
              />
              <Route
                path={NOT_AUTHORISED_PATH}
                element={<ScreensNotAuthorised />}
              />
              <Route path="*" element={<ScreensNotFound />} />
            </Routes>
          </AddesuLayout>
        </MsalProvider>
      </Suspense>
    </Router>
  );
};

export default ScreensRoot;
